/**
 * Day view: one row per robot, the day across.
 *
 * The whole day is laid out (00:00-24:00 on the SITE's clock); the
 * viewport shows the working hours (07:00-19:00) and the rest is a
 * horizontal scroll away, or the "24 h" toggle fits all of it. The robot
 * names and the hour labels are sticky, so neither scrolls away.
 *
 * KEYBOARD. The bars are one roving tab stop: Tab lands on one bar,
 * Left/Right move along the row in time order, Up/Down to the nearest bar
 * in the next row that has one, Home/End to the row's ends, Enter or
 * Space selects (they are real buttons).
 *
 * RENDERING. Rows are memoised and get only the numbers that concern
 * them (which of THEIR bars is selected or tabbable), so a click or a
 * focus move repaints two rows, not fifteen.
 *
 * DENSITY (2026-10-30). Compact rows are one line per robot: tasks too
 * close to draw apart merge into one count chip ("3", `mergeRow`) that
 * opens a list to pick one from. Comfortable rows stack them instead.
 *
 * DRAG (2026-10-30). A scheduled occurrence can be dragged along its own
 * row when the screen gave the move callback (`lane-drag.ts`); the drop
 * goes through the same "this time only, or the schedule?" question as
 * the panel's Move-to field, which stays the keyboard's way.
 */
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';
import { formatZonedClock } from '../zoned-time';
import { DIDNT_FINISH, OUTCOME_LABEL, paintOf, type LaneItem } from './lane-items';
import {
  COMFY_METRICS,
  COMPACT_METRICS,
  mergeRow,
  packRow,
  singleChip,
  type Axis,
  type LaneBoard,
  type LaneChip,
  type LaneMetrics,
  type LaneRow,
  type PackedRow,
  type Placement,
} from './lane-layout';
import {
  DRAG_THRESHOLD_PX,
  dragBounds,
  dragBubble,
  dragTarget,
  edgeScrollStep,
  isDraggable,
  isRowOutOfView,
  timeAtClientX,
  type DragGeometry,
} from './lane-drag';
import { LANE_CODE, barText, itemAriaLabel, laneVars } from './lane-style';
import { robotText } from './phone-model';
import {
  WORK_END_HOUR,
  WORK_START_HOUR,
  dayWindow,
  formatRange,
  hourTicks,
  instantAt,
  type DateKey,
  type TimeWindow,
} from './lane-time';
import type { SiteBar } from './site-row';
import { PriorityBadge } from '../TaskPriority';

const MIN_HOUR_W = 44;
/** jsdom and a first paint before measuring: a 13" laptop's lane width. */
const FALLBACK_VIEWPORT_W = 900;
const SNAP_MS = 15 * 60_000;
const SITE_MIN_PX = 72;
/** The narrowest bar that draws the High badge: below it the badge would
 *  be all the bar says. */
const HIGH_BADGE_MIN_W = 64;
/** Roughly what a site label needs at 10.5px, to choose long or short. */
const fits = (text: string, px: number) => text.length * 6.2 + 14 <= px;

export interface DayLanesProps {
  dateKey: DateKey;
  timeZone: string;
  nowMs: number;
  fullDay: boolean;
  board: LaneBoard;
  siteBars: readonly SiteBar[];
  collapsed: ReadonlySet<string>;
  onToggleGroup: (groupId: string) => void;
  selectedId: string | null;
  /** `trigger` is the chip the selection came from, for focus to return
   *  to when the panel closes. */
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
  /** An empty stretch of a lane was clicked. Withheld = no booking. */
  onCreateAtSlot?: (slot: { start: Date; end: Date }) => void;
  /** Only an occurrence still in the future can be skipped. */
  onSkipOccurrence?: (item: LaneItem) => void;
  /** One line per robot, close tasks merged into a count. */
  compact?: boolean;
  /** Dropping a dragged scheduled occurrence. Withheld = no dragging. */
  onMoveOccurrence?: (item: LaneItem, newStartMs: number) => void;
}

type Chip = LaneChip<LaneItem>;

type NavRow = {
  id: string;
  placements: Placement<{ id: string; startMs: number; endMs: number }>[];
};

/** An "Any robot" row's items carry no robot key to look up. */
const NO_ROBOT_LABEL = (): string | null => null;

function rowHeight(
  p: PackedRow<{ id: string; startMs: number; endMs: number }>,
  m: LaneMetrics,
): number {
  return Math.max(m.rowMinH, 2 * m.barTop - (m.barStep - m.barH) + p.subRows * m.barStep);
}

export function DayLanes(props: DayLanesProps) {
  const { dateKey, timeZone, nowMs, fullDay, board, siteBars, collapsed } = props;
  const compact = props.compact ?? false;
  const m = compact ? COMPACT_METRICS : COMFY_METRICS;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const [viewportW, setViewportW] = useState(0);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sync = () => setViewportW(el.clientWidth);
    sync();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const window = useMemo(() => dayWindow(dateKey, timeZone), [dateKey, timeZone]);
  // The hours drawn: where a dragged task may land.
  const dropHours = useMemo(
    () =>
      fullDay
        ? window
        : {
            fromMs: instantAt(dateKey, WORK_START_HOUR, timeZone),
            toMs: instantAt(dateKey, WORK_END_HOUR, timeZone),
          },
    [fullDay, window, dateKey, timeZone],
  );
  const hours = (window.toMs - window.fromMs) / 3_600_000;
  const trackViewport = Math.max(240, (viewportW || FALLBACK_VIEWPORT_W) - m.nameW);
  const hourW = fullDay
    ? trackViewport / hours
    : Math.max(MIN_HOUR_W, trackViewport / (WORK_END_HOUR - WORK_START_HOUR));
  const axis: Axis = useMemo(
    () => ({ fromMs: window.fromMs, toMs: window.toMs, pxPerMs: hourW / 3_600_000 }),
    [window, hourW],
  );
  const trackW = hours * hourW;
  const ticks = useMemo(() => hourTicks(dateKey, timeZone), [dateKey, timeZone]);
  const labelEvery = hourW >= 44 ? 1 : hourW >= 26 ? 2 : 3;

  // Open on the working hours -- or, today, on now when now is outside
  // them. Only when the day or the mode changes (or the first real
  // measurement lands), never on a data update or the minute tick.
  const positioned = useRef('');
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const key = `${dateKey}|${fullDay}|${viewportW > 0}|${m.nameW}`;
    if (positioned.current === key) return;
    positioned.current = key;
    if (fullDay) {
      el.scrollLeft = 0;
      return;
    }
    let target = instantAt(dateKey, WORK_START_HOUR, timeZone);
    const workEnd = instantAt(dateKey, WORK_END_HOUR, timeZone);
    if (nowMs >= window.fromMs && nowMs < window.toMs && (nowMs < target || nowMs > workEnd)) {
      target = nowMs - 3 * 3_600_000;
    }
    // A little before the hour, so its label is not under the names.
    el.scrollLeft = Math.max(0, (target - window.fromMs) * axis.pxPerMs - 20);
  }, [dateKey, fullDay, viewportW, timeZone, nowMs, window, axis.pxPerMs, m.nameW]);

  // ---- layout -----------------------------------------------------------
  // Site events are few and must be readable: wide enough for "Fire
  // drill" or "Blocked" whatever their length (the full words are in the
  // tooltip and the side panel).
  const sitePacked = useMemo(() => packRow(siteBars, axis, SITE_MIN_PX), [siteBars, axis]);
  const visibleRows = useMemo(() => {
    const rows: LaneRow[] = [];
    if (board.topAny) rows.push(board.topAny);
    for (const g of board.groups) if (!collapsed.has(g.id)) rows.push(...g.rows);
    return rows;
  }, [board, collapsed]);
  const packed = useMemo(() => {
    const out = new Map<string, PackedRow<Chip>>();
    for (const r of visibleRows)
      out.set(r.id, compact ? mergeRow(r.items, axis) : packRow(r.items.map(singleChip), axis));
    return out;
  }, [visibleRows, axis, compact]);

  // Navigation order: the Site row, then every drawn lane, top to bottom.
  const navRows: NavRow[] = useMemo(
    () => [
      { id: 'site', placements: sitePacked.placements },
      ...visibleRows.map((r) => ({ id: r.id, placements: packed.get(r.id)?.placements ?? [] })),
    ],
    [sitePacked, visibleRows, packed],
  );
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const firstKey = useMemo(() => {
    for (let r = 0; r < navRows.length; r += 1) if (navRows[r]!.placements.length) return `${r}:0`;
    return null;
  }, [navRows]);
  const activeKey = useMemo(() => {
    if (focusKey) {
      const [r, i] = focusKey.split(':').map(Number);
      if (navRows[r!]?.placements[i!]) return focusKey;
    }
    return firstKey;
  }, [focusKey, navRows, firstKey]);
  const tabbableIn = (rowIdx: number) => {
    if (!activeKey) return -1;
    const [r, i] = activeKey.split(':').map(Number);
    return r === rowIdx ? i! : -1;
  };
  const selectedIn = (rowIdx: number) => {
    const id = props.selectedId;
    if (!id) return -1;
    return navRows[rowIdx]!.placements.findIndex(
      (p) => p.item.id === id || (p.item as Partial<Chip>).items?.some((it) => it.id === id),
    );
  };

  const focusBar = (r: number, i: number) => {
    const el = canvasRef.current?.querySelector<HTMLElement>(`[data-nav="${r}:${i}"]`);
    if (el) {
      setFocusKey(`${r}:${i}`);
      el.focus();
      el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const nav = (e.target as HTMLElement).dataset?.nav;
    if (!nav) return;
    const [r, i] = nav.split(':').map(Number) as [number, number];
    const row = navRows[r];
    if (!row) return;
    const centre = (p: Placement<{ id: string; startMs: number; endMs: number }>) => p.x + p.w / 2;
    const vertical = (dir: 1 | -1) => {
      const from = row.placements[i];
      for (let rr = r + dir; rr >= 0 && rr < navRows.length; rr += dir) {
        const ps = navRows[rr]!.placements;
        if (!ps.length) continue;
        let best = 0;
        if (from) {
          let bestD = Infinity;
          ps.forEach((p, idx) => {
            const d = Math.abs(centre(p) - centre(from));
            if (d < bestD) {
              bestD = d;
              best = idx;
            }
          });
        }
        return focusBar(rr, best);
      }
    };
    switch (e.key) {
      case 'ArrowRight':
        if (i + 1 < row.placements.length) focusBar(r, i + 1);
        break;
      case 'ArrowLeft':
        if (i > 0) focusBar(r, i - 1);
        break;
      case 'Home':
        focusBar(r, 0);
        break;
      case 'End':
        focusBar(r, row.placements.length - 1);
        break;
      case 'ArrowDown':
        vertical(1);
        break;
      case 'ArrowUp':
        vertical(-1);
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const onCreate = props.onCreateAtSlot;
  const onTrackClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!onCreate || e.target !== e.currentTarget) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ms = window.fromMs + (e.clientX - rect.left) / axis.pxPerMs;
      const start = Math.min(
        window.toMs - SNAP_MS,
        window.fromMs + Math.max(0, Math.round((ms - window.fromMs) / SNAP_MS) * SNAP_MS),
      );
      onCreate({ start: new Date(start), end: new Date(start + 2 * SNAP_MS) });
    },
    [onCreate, window, axis.pxPerMs],
  );

  const nowX =
    nowMs >= window.fromMs && nowMs < window.toMs
      ? m.nameW + (nowMs - window.fromMs) * axis.pxPerMs
      : null;

  let rowIdx = 1;
  const rowView = (row: LaneRow) => {
    const idx = rowIdx++;
    const p = packed.get(row.id)!;
    return (
      <LaneTrackRow
        key={row.id}
        row={row}
        packed={p}
        rowIdx={idx}
        trackW={trackW}
        hourW={hourW}
        timeZone={timeZone}
        tabbable={tabbableIn(idx)}
        selected={selectedIn(idx)}
        onSelect={props.onSelect}
        onFocusBar={setFocusKey}
        onTrackClick={onCreate ? onTrackClick : undefined}
        onSkip={props.onSkipOccurrence}
        metrics={m}
        pxPerMs={axis.pxPerMs}
        axisFromMs={window.fromMs}
        hours={dropHours}
        onMove={props.onMoveOccurrence}
      />
    );
  };

  return (
    <div className="nx-lanes__scroll" ref={scrollRef} data-testid="cal-day-scroller">
      <div
        className="nx-lanes__canvas"
        ref={canvasRef}
        style={{ width: m.nameW + trackW }}
        onKeyDown={onKeyDown}
        role="grid"
        aria-label="Tasks by robot, across the day"
        aria-rowcount={navRows.length + 1}
        data-testid="cal-day-lanes"
        data-hour-px={Math.round(hourW)}
      >
        <div className="nx-lanes__head" role="row">
          <div className="nx-lanes__corner" role="columnheader">
            {board.robotCount === 1 ? '1 robot' : `${board.robotCount} robots`}
          </div>
          <div className="nx-lanes__hours" style={{ width: trackW }} aria-hidden="true">
            {ticks.map((t, i) =>
              // Every hour gets a grid line; only as many get a label as
              // fit (the 24 h view labels every second or third hour).
              i % labelEvery !== 0 ? null : (
                <span
                  key={t.ms}
                  className="nx-lanes__hour"
                  style={{ left: (t.ms - window.fromMs) * axis.pxPerMs }}
                >
                  {t.label}
                </span>
              ),
            )}
          </div>
        </div>

        <div className="nx-lanes__row nx-lanes__row--site" role="row" data-testid="cal-site-row">
          <div className="nx-lanes__name" role="rowheader">
            <span className="nx-lanes__name-text">Site</span>
          </div>
          <div
            className="nx-lanes__track"
            style={{
              width: trackW,
              height: Math.max(m.siteMinH, rowHeight(sitePacked, m)),
              backgroundSize: `${hourW}px 100%`,
            }}
            role="gridcell"
          >
            {sitePacked.placements.map((p, i) => {
              const bar = p.item as SiteBar;
              const when = formatRange(bar.startMs, bar.endMs, timeZone);
              const label = `${bar.label}, ${when}${bar.ongoing ? ', still open' : ''}`;
              return (
                <button
                  key={bar.id}
                  type="button"
                  className={`nx-lanes__site-bar nx-lanes__site-bar--${bar.kind}${bar.liftRule?.kind === 'allow_travel' ? ' is-allow' : ''}${props.selectedId === bar.id ? ' is-selected' : ''}`}
                  style={{
                    left: p.x,
                    top: m.barTop + 1 + p.sub * m.barStep,
                    width: p.w,
                    height: m.barH,
                  }}
                  data-nav={`0:${i}`}
                  tabIndex={tabbableIn(0) === i ? 0 : -1}
                  aria-label={label}
                  aria-pressed={props.selectedId === bar.id}
                  title={label}
                  onFocus={() => setFocusKey(`0:${i}`)}
                  onClick={(e) => props.onSelect(bar.id, e.currentTarget)}
                  data-testid={
                    bar.maintenance
                      ? `cal-maint-${bar.maintenance.window_id}`
                      : bar.liftRule
                        ? `cal-lift-rule-${bar.liftRule.window_id}`
                        : `cal-fire-${bar.fire!.fire_event_id}`
                  }
                >
                  {p.clippedStart ? '‹ ' : ''}
                  <span className="nx-lanes__site-text">
                    {fits(bar.label, p.w) ? bar.label : bar.shortLabel}
                  </span>
                  {p.clippedEnd ? ' ›' : ''}
                </button>
              );
            })}
          </div>
        </div>

        {board.topAny && rowView(board.topAny)}

        {board.groups.map((g) => {
          const closed = collapsed.has(g.id);
          const summary = `${g.robotCount === 1 ? '1 robot' : `${g.robotCount} robots`} · ${
            g.taskCount === 1 ? '1 task' : `${g.taskCount} tasks`
          }${g.didntFinish ? ` · ${g.didntFinish} didn’t finish` : ''}`;
          return (
            <div key={g.id} role="rowgroup" className="nx-lanes__group">
              <div role="row" className="nx-lanes__group-row">
                <button
                  type="button"
                  className="nx-lanes__group-head"
                  aria-expanded={!closed}
                  onClick={() => props.onToggleGroup(g.id)}
                  data-testid={`cal-group-${g.id}`}
                >
                  <span aria-hidden="true">{closed ? '▸' : '▾'}</span>
                  <span className="nx-lanes__group-name">{g.name}</span>
                  <span className={`nx-lanes__group-sum${g.didntFinish ? ' has-fail' : ''}`}>
                    {summary}
                  </span>
                </button>
              </div>
              {!closed && g.rows.map(rowView)}
            </div>
          );
        })}

        {board.groups.length === 0 && !board.topAny && (
          <p className="nx-lanes__empty" data-testid="cal-lanes-empty">
            No robots match. Clear “Find robot” to see every lane.
          </p>
        )}

        {nowX !== null && (
          <div
            className="nx-lanes__now"
            style={{ left: nowX }}
            aria-hidden="true"
            data-testid="cal-now"
            title={`Now, ${formatZonedClock(nowMs, timeZone)}`}
          />
        )}
      </div>
    </div>
  );
}

interface LaneTrackRowProps {
  row: LaneRow;
  packed: PackedRow<Chip>;
  rowIdx: number;
  trackW: number;
  hourW: number;
  timeZone: string;
  /** Which of this row's chips is the roving tab stop, or -1. */
  tabbable: number;
  /** Which of this row's chips is (or holds) the selection, or -1. */
  selected: number;
  onSelect: (id: string, trigger?: HTMLElement | null) => void;
  onFocusBar: (key: string) => void;
  onTrackClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onSkip?: (item: LaneItem) => void;
  metrics: LaneMetrics;
  pxPerMs: number;
  /** The time at the track's x = 0. */
  axisFromMs: number;
  /** The hours drawn (07-19, or the whole day): a drop lands inside them. */
  hours: TimeWindow;
  onMove?: (item: LaneItem, newStartMs: number) => void;
}

interface Press {
  item: LaneItem;
  pointerId: number;
  startX: number;
  /** The pointer's last client x: a scroll re-reads the time under it. */
  lastX: number;
  /** Where on the chip it was grabbed, as time from its start. */
  grabOffsetMs: number;
  bounds: TimeWindow;
  scroller: HTMLElement | null;
  active: boolean;
  cancelled: boolean;
}

/** Roughly the drag bubble's size, px, to keep it inside the view. */
const BUBBLE_W = 210;
const BUBBLE_H = 48;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const LaneTrackRow = memo(function LaneTrackRow({
  row,
  packed,
  rowIdx,
  trackW,
  hourW,
  timeZone,
  tabbable,
  selected,
  onSelect,
  onFocusBar,
  onTrackClick,
  onSkip,
  metrics: m,
  pxPerMs,
  axisFromMs,
  hours,
  onMove,
}: LaneTrackRowProps) {
  const h = rowHeight(packed, m);
  // An "Any robot" row's label names the kind its work needs
  // (`anyRowLabel`): "Any cleaning robot".
  const who = row.label;
  // ...but a bar in that row speaks for its own item (review BB-B-1,
  // 2026-10-31): a run with no robot pinned is "Any cleaning robot", a
  // task the fleet has not given a robot is "Not assigned yet" (ended
  // without one: "Not assigned"), as the lane panel, the Agenda, the
  // phone and the drawers say (Akash, 2026-10-31). Its hover and its
  // screen-reader label both read the row's label before.
  const whoOf = (it: LaneItem): string => (row.kind === 'any' ? robotText(it, NO_ROBOT_LABEL) : who);
  const fails = row.items.filter((it) => DIDNT_FINISH.has(it.outcome)).length;

  // ---- drag ---------------------------------------------------------------
  const rowRef = useRef<HTMLDivElement | null>(null);
  const press = useRef<Press | null>(null);
  const [drag, setDrag] = useState<{
    id: string;
    newStart: number;
    /** Where the bubble goes so it stays inside the lanes' view. */
    above: boolean;
    alignRight: boolean;
    /** The right edge of what is on screen, track px. */
    viewRight: number;
  } | null>(null);
  /** The click that ends a drag must not also select. */
  const swallowClick = useRef(false);

  const geometry = (sc: HTMLElement | null): DragGeometry => ({
    scrollerLeft: (sc?.getBoundingClientRect().left ?? 0) + (sc?.clientLeft ?? 0),
    scrollLeft: sc?.scrollLeft ?? 0,
    nameW: m.nameW,
    axisFromMs,
    pxPerMs,
  });
  /** The pointer is at `clientX` (or the lanes scrolled under it). */
  const retarget = (d: Press, clientX: number) => {
    d.lastX = clientX;
    const g = geometry(d.scroller);
    const newStart = dragTarget(clientX, d.grabOffsetMs, g, d.bounds);
    // Keep the bubble in view: above the chip on a row near the bottom of
    // the lanes, ending at the chip's right edge near their right edge.
    const view = d.scroller?.getBoundingClientRect();
    const row = rowRef.current?.getBoundingClientRect();
    const chipLeft =
      g.scrollerLeft + g.nameW - g.scrollLeft + (newStart - g.axisFromMs) * g.pxPerMs;
    const above = Boolean(view && row && view.height > 0 && row.bottom + BUBBLE_H > view.bottom);
    const alignRight = Boolean(view && view.width > 0 && chipLeft + BUBBLE_W > view.right);
    const viewRight = view && view.width > 0 ? g.scrollLeft + view.width - g.nameW - 8 : Infinity;
    setDrag({ id: d.item.id, newStart, above, alignRight, viewRight });
    return newStart;
  };
  const cancelDrag = (d: Press) => {
    d.cancelled = true;
    setDrag(null);
  };
  // The effects below run once per drag; they reach today's props
  // through this ref.
  const live = useRef({ retarget, cancelDrag, geometry });
  live.current = { retarget, cancelDrag, geometry };

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;
    // Esc cancels the drag (and only the drag: the panel stays open).
    const onKey = (e: globalThis.KeyboardEvent) => {
      const d = press.current;
      if (e.key !== 'Escape' || !d?.active || d.cancelled) return;
      live.current.cancelDrag(d);
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    // No text selection while a chip is being dragged (Shift held, or a
    // fast sweep across the names).
    const body = document.body.style;
    const before = body.userSelect;
    body.userSelect = 'none';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      body.userSelect = before;
    };
  }, [dragging]);

  useEffect(() => {
    const sc = press.current?.scroller;
    if (!dragging || !sc) return;
    // The lanes scrolled under a still pointer: re-read the time under it
    // -- or, once the row has gone wholly out of view, drop the drag
    // rather than carry on with a chip nobody can see.
    const onScroll = () => {
      const d = press.current;
      if (!d?.active || d.cancelled) return;
      const row = rowRef.current?.getBoundingClientRect();
      const view = sc.getBoundingClientRect();
      const head = sc.querySelector('.nx-lanes__head')?.getBoundingClientRect().height ?? 0;
      if (row && isRowOutOfView(row, view.top + head, view.bottom)) {
        live.current.cancelDrag(d);
        return;
      }
      live.current.retarget(d, d.lastX);
    };
    sc.addEventListener('scroll', onScroll);
    // Near the left or right edge the drag scrolls the lanes itself,
    // while the hours it may land in go on past the edge. Not with
    // reduced motion: the drop is clamped to the hours drawn anyway.
    let frame = 0;
    const tick = () => {
      const d = press.current;
      if (d?.active && !d.cancelled) {
        const step = edgeScrollStep({
          clientX: d.lastX,
          g: live.current.geometry(sc),
          clientWidth: sc.clientWidth,
          maxScrollLeft: sc.scrollWidth - sc.clientWidth,
          bounds: d.bounds,
        });
        if (step !== 0) sc.scrollLeft += step;
      }
      frame = requestAnimationFrame(tick);
    };
    if (!prefersReducedMotion()) frame = requestAnimationFrame(tick);
    return () => {
      sc.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [dragging]);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>, it: LaneItem) => {
    if (!onMove || !isDraggable(it) || e.button !== 0 || e.pointerType === 'touch') return;
    const el = e.currentTarget;
    const scroller = el.closest<HTMLElement>('.nx-lanes__scroll');
    press.current = {
      item: it,
      pointerId: e.pointerId,
      startX: e.clientX,
      lastX: e.clientX,
      grabOffsetMs: timeAtClientX(e.clientX, geometry(scroller)) - it.startMs,
      bounds: dragBounds(hours, it.startMs),
      scroller,
      active: false,
      cancelled: false,
    };
    if (typeof el.setPointerCapture === 'function') el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = press.current;
    if (!d || d.cancelled || e.pointerId !== d.pointerId) return;
    if (!d.active) {
      if (Math.abs(e.clientX - d.startX) < DRAG_THRESHOLD_PX) return;
      d.active = true;
    }
    retarget(d, e.clientX);
  };
  const endPress = (e: PointerEvent<HTMLButtonElement>, drop: boolean) => {
    const d = press.current;
    if (!d || e.pointerId !== d.pointerId) return;
    press.current = null;
    const el = e.currentTarget;
    if (typeof el.hasPointerCapture === 'function' && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    if (!d.active) return; // a click: it selects as usual
    swallowClick.current = true;
    setTimeout(() => {
      swallowClick.current = false;
    }, 0);
    setDrag(null);
    if (!drop || d.cancelled) return;
    const newStart = dragTarget(e.clientX, d.grabOffsetMs, geometry(d.scroller), d.bounds);
    if (newStart !== d.item.startMs) onMove?.(d.item, newStart);
  };

  // ---- count chips --------------------------------------------------------
  const [openList, setOpenList] = useState<string | null>(null);
  const listAnchor = useRef<HTMLElement | null>(null);

  return (
    <div
      ref={rowRef}
      className={`nx-lanes__row${row.kind === 'any' ? ' nx-lanes__row--any' : ''}`}
      role="row"
      data-testid={`cal-lane-${row.id}`}
    >
      <div className="nx-lanes__name" role="rowheader" style={{ height: h }}>
        <span
          className="nx-lanes__name-text"
          title={row.robot ? `${row.robot.name} · ${row.robot.fleet}` : undefined}
        >
          {who}
        </span>
        <span className="nx-lanes__name-meta">
          {row.robot?.level ?? ''}
          <span className="nx-lanes__sr">
            {`, ${row.items.length === 1 ? '1 task' : `${row.items.length} tasks`}${fails ? `, ${fails} didn’t finish` : ''}`}
          </span>
        </span>
      </div>
      <div
        className="nx-lanes__track"
        role="gridcell"
        style={{ width: trackW, height: h, backgroundSize: `${hourW}px 100%` }}
        onClick={onTrackClick}
        data-testid={`cal-lane-track-${row.id}`}
      >
        {packed.placements.map((p, i) => {
          const chip = p.item;
          const top = m.barTop + p.sub * m.barStep;
          if (chip.items.length > 1) {
            const open = openList === chip.id;
            return (
              <CountChip
                key={chip.id}
                chip={chip}
                who={who}
                x={p.x}
                w={p.w}
                top={top}
                height={m.barH}
                timeZone={timeZone}
                nav={`${rowIdx}:${i}`}
                tabbable={i === tabbable}
                isSelected={i === selected}
                open={open}
                onFocus={() => onFocusBar(`${rowIdx}:${i}`)}
                onToggle={(el) => {
                  listAnchor.current = el;
                  setOpenList(open ? null : chip.id);
                }}
                onClose={(refocus) => {
                  setOpenList(null);
                  if (refocus) listAnchor.current?.focus();
                }}
                onPick={(it) => {
                  setOpenList(null);
                  const anchor = listAnchor.current;
                  onSelect(it.id, anchor);
                  anchor?.focus();
                }}
              />
            );
          }
          const it = chip.items[0]!;
          const label = itemAriaLabel(it, whoOf(it), timeZone);
          const skippable =
            Boolean(onSkip) && it.kind === 'occurrence' && it.outcome === 'scheduled' && p.w >= 44;
          const draggable = Boolean(onMove) && isDraggable(it);
          const moved = drag?.id === it.id ? drag : null;
          const left = moved ? p.x + (moved.newStart - it.startMs) * pxPerMs : p.x;
          const bubble = moved ? dragBubble(it, moved.newStart, row.items, timeZone) : null;
          return (
            <span key={it.id} className="nx-lanes__bar-slot">
              {moved && (
                <span
                  className="nx-lanes__drag-origin"
                  style={{ left: p.x, top, width: p.w, height: m.barH }}
                  aria-hidden="true"
                />
              )}
              <span
                className={`nx-lanes__bar-wrap${moved ? ' is-dragging' : ''}`}
                style={{
                  left,
                  top,
                  width: p.w,
                  height: m.barH,
                  ...laneVars(paintOf(it)),
                }}
              >
                <button
                  type="button"
                  className={`nx-lanes__bar${i === selected ? ' is-selected' : ''}`}
                  data-outcome={it.outcome}
                  data-kind={it.kind}
                  data-high={it.high ? 'true' : undefined}
                  data-draggable={draggable ? 'true' : undefined}
                  data-nav={`${rowIdx}:${i}`}
                  tabIndex={i === tabbable ? 0 : -1}
                  aria-label={label}
                  aria-pressed={i === selected}
                  title={label}
                  onFocus={() => onFocusBar(`${rowIdx}:${i}`)}
                  onClick={(e) => {
                    if (swallowClick.current) return;
                    onSelect(it.id, e.currentTarget);
                  }}
                  onPointerDown={draggable ? (e) => onPointerDown(e, it) : undefined}
                  onPointerMove={draggable ? onPointerMove : undefined}
                  onPointerUp={draggable ? (e) => endPress(e, true) : undefined}
                  onPointerCancel={draggable ? (e) => endPress(e, false) : undefined}
                  data-testid={`cal-event-${it.id}`}
                >
                  {p.clippedStart ? '‹' : ''}
                  {/* High: a corner mark on every High bar (the CSS's), and
                      the word as well where the bar has room for it beside
                      another; the bar's name says it either way. */}
                  {it.high && p.w >= HIGH_BADGE_MIN_W && (
                    <PriorityBadge compact testId={`cal-event-high-${it.id}`} />
                  )}
                  {barText(
                    it,
                    p.w - (skippable ? 18 : 0) - (it.high && p.w >= HIGH_BADGE_MIN_W ? 32 : 0),
                  )}
                  {p.clippedEnd ? '›' : ''}
                </button>
                {skippable && !moved && (
                  <button
                    type="button"
                    className="nx-lanes__skip"
                    aria-label={`Skip ${it.title} at ${formatZonedClock(it.startMs, timeZone)}`}
                    title="Skip this one, or delete the series"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip!(it);
                    }}
                    data-testid={`cal-event-delete-${it.id}`}
                  >
                    ×
                  </button>
                )}
              </span>
              {bubble && (
                <span
                  className={`nx-lanes__drag-bubble${moved!.above ? ' is-above' : ''}${
                    moved!.alignRight ? ' is-right' : ''
                  }`}
                  style={{
                    left: moved!.alignRight ? Math.min(left + p.w, moved!.viewRight) : left,
                    top: moved!.above ? top - 4 : top + m.barH + 4,
                  }}
                  role="status"
                  data-testid="cal-drag-bubble"
                >
                  <span data-testid="cal-drag-when">{bubble.when}</span>
                  {bubble.clash && (
                    <span className="nx-lanes__drag-clash">
                      <span aria-hidden="true">⚠ </span>
                      <span data-testid="cal-drag-clash">{bubble.clash}</span>
                    </span>
                  )}
                </span>
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
});

/**
 * Several tasks too close to draw apart, as one neutral count ("3").
 * Pressing it opens a short list to pick one from; the pick is selected
 * (and shown in the panel) and focus comes back to the chip. Esc or a
 * click elsewhere closes the list. The chip's name and tooltip list what
 * it holds, so nothing in it is only reachable by opening it.
 */
function CountChip({
  chip,
  who,
  x,
  w,
  top,
  height,
  timeZone,
  nav,
  tabbable,
  isSelected,
  open,
  onFocus,
  onToggle,
  onClose,
  onPick,
}: {
  chip: Chip;
  who: string;
  x: number;
  w: number;
  top: number;
  height: number;
  timeZone: string;
  nav: string;
  tabbable: boolean;
  isSelected: boolean;
  open: boolean;
  onFocus: () => void;
  onToggle: (el: HTMLElement) => void;
  onClose: (refocus: boolean) => void;
  onPick: (item: LaneItem) => void;
}) {
  const items = chip.items;
  const fails = items.filter((it) => DIDNT_FINISH.has(it.outcome)).length;
  const highs = items.filter((it) => it.high).length;
  const when = formatRange(chip.startMs, chip.endMs, timeZone);
  const label = `${who}: ${items.length} tasks too close to draw apart, ${when}${
    fails ? `, ${fails} didn’t finish` : ''
  }${highs ? `, ${highs} High priority` : ''}. Open the list.`;
  const line = (it: LaneItem) =>
    `${formatZonedClock(it.startMs, timeZone)} ${it.title}${it.high ? ' (High)' : ''} — ${OUTCOME_LABEL[it.outcome]}`;
  const listRef = useRef<HTMLDivElement | null>(null);
  const chipRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onDown = (e: globalThis.MouseEvent) => {
      const t = e.target as Node;
      if (listRef.current?.contains(t) || chipRef.current?.contains(t)) return;
      closeRef.current(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <>
      <span className="nx-lanes__bar-wrap" style={{ left: x, top, width: w, height }}>
        <button
          ref={chipRef}
          type="button"
          className={`nx-lanes__bar nx-lanes__count${isSelected ? ' is-selected' : ''}`}
          data-fail={fails ? 'true' : undefined}
          data-high={highs ? 'true' : undefined}
          data-nav={nav}
          tabIndex={tabbable ? 0 : -1}
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={`${items.length} tasks:\n${items.map(line).join('\n')}`}
          onFocus={onFocus}
          onClick={(e) => onToggle(e.currentTarget)}
          data-testid={`cal-count-${chip.id}`}
        >
          {items.length}
        </button>
      </span>
      {open && (
        <div
          ref={listRef}
          className="nx-lanes__count-list"
          role="dialog"
          aria-label={`${items.length} tasks, ${when}`}
          style={{ left: x, top: top + height + 4 }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              onClose(true);
            }
          }}
          data-testid="cal-count-list"
        >
          <span className="nx-lanes__count-head">
            {items.length} tasks · {when}
          </span>
          <ul>
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  className={DIDNT_FINISH.has(it.outcome) ? 'is-fail' : undefined}
                  onClick={() => onPick(it)}
                  data-testid={`cal-count-pick-${it.id}`}
                >
                  <span className="nx-lanes__count-code" style={laneVars(paintOf(it))}>
                    {LANE_CODE[paintOf(it)]}
                  </span>
                  <span className="nx-lanes__count-text">{line(it)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
